import { LoadingButton } from '@mui/lab';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TextField } from '@mui/material';
import axios from 'axios';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { extendIssueDuration } from 'src/api/stoApi';
import { Asset } from 'src/models';
import { Colors } from 'src/components/utils/Theme';

interface Props {
  asset: Asset;
  open: boolean;
  handleClose: () => void;
  triggerRefresh: () => void;
}

export const ExtendDurationDialog: FC<Props> = ({ asset, open, triggerRefresh, handleClose }) => {
  const { t } = useTranslation();
  const [dialogState, setDialogState] = useState<'init' | 'error' | 'success'>('init');
  const [error, setError] = useState<string>('');
  const [days, setDays] = useState<string>('35');
  const [isLoading, setLoading] = useState(false);
  const handleCloseAndReset = () => {
    handleClose();
    setDialogState('init');
    setError('');
  };

  const requestDurationExtension = async () => {
    setLoading(true);
    try {
      const changeStatus = await extendIssueDuration(asset.id, days);
      if (changeStatus.data.status === 200) {
        setDialogState('success');
        triggerRefresh();
      } else {
        setDialogState('error');
        setError(changeStatus.data.message);
      }
    } catch (error) {
      setDialogState('error');
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data.error);
        if (error.response.data.message) {
          setError(error.response.data?.message);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseAndReset}>
      {dialogState === 'init' && (
        <>
          <DialogTitle>{t('dashboard.assets.dialog.extendduration.init.title')}</DialogTitle>
          <DialogContent>
            <Typography>{t('dashboard.assets.dialog.extendduration.init.message')}</Typography>
            <TextField
              margin="normal"
              label={t('dashboard.assets.dialog.extendduration.init.days')}
              type="number"
              variant={'outlined'}
              fullWidth
              value={days}
              onChange={(e) => {
                if (e.target.value.match(/^[0-9]*$/)) {
                  setDays(e.target.value);
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.extendduration.init.cancel')}
            </Button>
            <LoadingButton
              loading={isLoading}
              variant="contained"
              disableElevation
              color="primary"
              onClick={requestDurationExtension}
              disabled={days.length <= 0}
            >
              {t('dashboard.assets.dialog.extendduration.init.confirm')}
            </LoadingButton>
          </DialogActions>
        </>
      )}
      {dialogState === 'error' && (
        <>
          <DialogTitle>{t('dashboard.assets.dialog.extendduration.error.headline')}</DialogTitle>
          <DialogContent>
            <Typography>{t('dashboard.assets.dialog.extendduration.error.message')}</Typography>
            <Typography color={Colors.ERROR}>{error}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" disableElevation color="primary" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.extendduration.error.ok')}
            </Button>
          </DialogActions>
        </>
      )}

      {dialogState === 'success' && (
        <>
          <DialogTitle>{t('dashboard.assets.dialog.extendduration.success.headline')}</DialogTitle>
          <DialogContent>
            <Typography>{t('dashboard.assets.dialog.extendduration.success.message')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" disableElevation color="primary" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.extendduration.success.ok')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
